import { useMemo, useState, type DragEvent, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { LEAD_ORIGIN_LABELS, LEAD_ORIGINS, LEAD_STAGE_LABELS, LEAD_STAGES, MOTIVOS_PERDA, type LeadOrigin, type LeadStage } from '@agencia/shared';
import { useCurrentUser } from '@/auth/AuthContext';
import { Alert, Button, Field } from '@/components/ui';
import { api } from '@/lib/api';
import { ORIGIN_TONE, quando, retornoAtrasado, type LeadCard } from '@/lib/crm';
import { errorMessage } from '@/lib/errors';
import { formatBRL } from '@/lib/format';
import { useApi } from '@/lib/useApi';

interface Board {
  cards: LeadCard[];
  parados: number;
  retornosPendentes: number;
  semConsultor: number;
}

/** Funil de clientes: colunas por etapa, arrastar para mudar de etapa. */
export function ClientesPage() {
  const me = useCurrentUser();
  const gestao = me.role === 'ADMIN' || me.role === 'GESTOR';
  const [busca, setBusca] = useState('');
  const [consultor, setConsultor] = useState('');
  const [verParados, setVerParados] = useState(false);
  const qs = new URLSearchParams({ ...(busca.trim() ? { busca: busca.trim() } : {}), ...(consultor ? { consultor } : {}), ...(verParados ? { parados: '1' } : {}) });
  const { data, error, reload, setData } = useApi<Board>(`/crm/leads?${qs.toString()}`, 30_000);
  const [actionError, setActionError] = useState<string | null>(null);
  const [perda, setPerda] = useState<LeadCard | null>(null);
  const [novo, setNovo] = useState(false);
  const navigate = useNavigate();

  const colunas = useMemo(() => {
    const m = new Map<LeadStage, LeadCard[]>(LEAD_STAGES.map((s) => [s, []]));
    for (const c of data?.cards ?? []) m.get(c.etapa)?.push(c);
    return m;
  }, [data]);

  const consultores = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of data?.cards ?? []) if (c.consultor) m.set(c.consultor.id, c.consultor.nome);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1], 'pt-BR'));
  }, [data]);

  async function mover(card: LeadCard, etapa: LeadStage, motivoPerda?: string) {
    if (card.etapa === etapa) return;
    if (etapa === 'PERDEU' && !motivoPerda) return setPerda(card);
    setActionError(null);
    // Atualiza na hora (otimista) e confirma com o servidor.
    setData((d) => (d ? { ...d, cards: d.cards.map((c) => (c.id === card.id ? { ...c, etapa, motivoPerda: motivoPerda ?? c.motivoPerda } : c)) } : d));
    try {
      await api.patch(`/crm/leads/${card.id}`, { etapa, ...(motivoPerda ? { motivoPerda } : {}) });
      if (etapa === 'FECHOU') navigate(`/clientes/${card.id}?ligarVenda=1`);
    } catch (err) {
      setActionError(errorMessage(err));
      void reload();
    }
  }

  function onDrop(e: DragEvent, etapa: LeadStage) {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain');
    const card = data?.cards.find((c) => c.id === id);
    if (card) void mover(card, etapa);
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{gestao ? 'Clientes' : 'Meus clientes'}</h1>
          <p className="muted">Novos atendimentos chegam sozinhos da Digisac. Arraste o cartão para mudar de etapa.</p>
        </div>
        <div className="row">
          <input className="search" type="search" placeholder="Buscar por nome ou telefone" aria-label="Buscar cliente" value={busca} onChange={(e) => setBusca(e.target.value)} />
          {gestao && (
            <select aria-label="Filtrar por consultor" value={consultor} onChange={(e) => setConsultor(e.target.value)}>
              <option value="">Todos os consultores</option>
              <option value="sem">Sem consultor</option>
              {consultores.map(([id, nome]) => (
                <option key={id} value={id}>
                  {nome}
                </option>
              ))}
            </select>
          )}
          <Button onClick={() => setNovo(true)}>+ Novo cliente</Button>
        </div>
      </header>

      {(error ?? actionError) && <Alert>{error ?? actionError}</Alert>}
      {data && (data.retornosPendentes > 0 || data.semConsultor > 0 || data.parados > 0 || verParados) && (
        <div className="row">
          {(data.parados > 0 || verParados) && (
            <button type="button" className="chip chip--gray" onClick={() => setVerParados((v) => !v)}>
              {verParados ? 'Esconder clientes parados' : `${data.parados} novo(s) parado(s) há mais de 21 dias`}
            </button>
          )}
          {data.retornosPendentes > 0 && <span className="chip chip--orange">{data.retornosPendentes} retorno(s) para hoje ou atrasados</span>}
          {data.semConsultor > 0 && (
            <button type="button" className="chip chip--gray" onClick={() => setConsultor('sem')}>
              {data.semConsultor} cliente(s) sem consultor ligado
            </button>
          )}
        </div>
      )}

      {novo && <NovoCliente onClose={() => setNovo(false)} onCreated={(id) => navigate(`/clientes/${id}`)} />}
      {perda && (
        <MotivoPerda
          card={perda}
          onCancel={() => setPerda(null)}
          onConfirm={(m) => {
            const c = perda;
            setPerda(null);
            void mover(c, 'PERDEU', m);
          }}
        />
      )}

      {!data ? (
        !error && <p className="muted">Carregando…</p>
      ) : (
        <section className="kanban" aria-label="Funil de clientes">
          {LEAD_STAGES.map((etapa) => {
            const cards = colunas.get(etapa) ?? [];
            const total = cards.reduce((acc, c) => acc + Number(c.valorEstimado ?? 0), 0);
            return (
              <div key={etapa} className={`kanban__col kanban__col--${etapa.toLowerCase()}`} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, etapa)}>
                <div className="kanban__head">
                  <h2>{LEAD_STAGE_LABELS[etapa]}</h2>
                  <span>
                    {cards.length}
                    {total > 0 && ` · ${formatBRL(total)}`}
                  </span>
                </div>
                {(etapa === 'FECHOU' || etapa === 'PERDEU') && <p className="kanban__hint">Últimos 30 dias</p>}
                {cards.map((c) => (
                  <Cartao key={c.id} card={c} showConsultor={gestao} onMover={(e) => void mover(c, e)} />
                ))}
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
}

function Cartao({ card, showConsultor, onMover }: { card: LeadCard; showConsultor: boolean; onMover: (e: LeadStage) => void }) {
  const retorno = retornoAtrasado(card.retornoEm);
  return (
    <article
      className={`lead-card ${retorno ? 'lead-card--retorno' : ''}`}
      draggable
      onDragStart={(e) => e.dataTransfer.setData('text/plain', card.id)}
    >
      <div className="lead-card__top">
        <Link to={`/clientes/${card.id}`} className="lead-card__name">
          {card.nome}
        </Link>
        <span className={`lead-card__when ${retorno ? 'is-alert' : ''}`}>
          {retorno === 'atrasado' ? 'Retorno atrasado' : retorno === 'hoje' ? 'Retornar hoje' : quando(card.ultimaMensagemEm ?? card.primeiroContatoEm)}
        </span>
      </div>
      {card.resumo ? (
        <span className="lead-card__text">{card.resumo}</span>
      ) : (
        card.primeiraMensagem && <span className="lead-card__text">"{card.primeiraMensagem.slice(0, 110)}{card.primeiraMensagem.length > 110 ? '…' : ''}"</span>
      )}
      {card.valorEstimado && <strong className="lead-card__value">{formatBRL(card.valorEstimado)}</strong>}
      {card.iaMotivo && (
        <span className="ia-pill">
          <IaIcon /> {card.iaMotivo}
        </span>
      )}
      {card.etapa === 'PERDEU' && card.motivoPerda && <span className="tag-pill tag-pill--gray">{card.motivoPerda}</span>}
      <div className="lead-card__foot">
        <span className={`tag-pill tag-pill--${ORIGIN_TONE[card.origem]}`}>{card.originLinkNome ?? LEAD_ORIGIN_LABELS[card.origem]}</span>
        {showConsultor && <span className="lead-card__who">{card.consultor?.nome.split(' ')[0] ?? 'Sem consultor'}</span>}
      </div>
      {/* Alternativa ao arrastar (celular e teclado). */}
      <select className="lead-card__move" aria-label={`Mover ${card.nome} para`} value={card.etapa} onChange={(e) => onMover(e.target.value as LeadStage)}>
        {LEAD_STAGES.map((s) => (
          <option key={s} value={s}>
            {LEAD_STAGE_LABELS[s]}
          </option>
        ))}
      </select>
    </article>
  );
}

export function IaIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" style={{ flex: 'none' }}>
      <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
    </svg>
  );
}

function MotivoPerda({ card, onCancel, onConfirm }: { card: LeadCard; onCancel: () => void; onConfirm: (m: string) => void }) {
  const [motivo, setMotivo] = useState<string>(MOTIVOS_PERDA[0]);
  return (
    <section className="card highlight" aria-labelledby="perda-t">
      <h2 id="perda-t">Por que perdemos {card.nome}?</h2>
      <div className="row">
        <select aria-label="Motivo da perda" value={motivo} onChange={(e) => setMotivo(e.target.value)}>
          {MOTIVOS_PERDA.map((m) => (
            <option key={m}>{m}</option>
          ))}
        </select>
        <Button onClick={() => onConfirm(motivo)}>Marcar como perdido</Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </section>
  );
}

function NovoCliente({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [origem, setOrigem] = useState<LeadOrigin>('WHATSAPP');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const r = await api.post<{ id: string }>('/crm/leads', { nome, telefone: telefone || undefined, origem });
      onCreated(r.id);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }
  return (
    <section className="card" aria-labelledby="novo-c">
      <h2 id="novo-c">Novo cliente</h2>
      <p className="muted small">Use para quem não chegou pela Digisac (telefone, loja, indicação pessoal).</p>
      <form className="grid-form" onSubmit={(e) => void submit(e)}>
        {error && <Alert>{error}</Alert>}
        <Field label="Nome" value={nome} onChange={(e) => setNome(e.target.value)} required autoFocus />
        <Field label="WhatsApp" value={telefone} onChange={(e) => setTelefone(e.target.value)} placeholder="(75) 9 9999-9999" />
        <div className="field">
          <label htmlFor="novo-origem">Origem</label>
          <select id="novo-origem" value={origem} onChange={(e) => setOrigem(e.target.value as LeadOrigin)}>
            {LEAD_ORIGINS.map((o) => (
              <option key={o} value={o}>
                {LEAD_ORIGIN_LABELS[o]}
              </option>
            ))}
          </select>
        </div>
        <div className="row">
          <Button type="submit" loading={saving}>
            Cadastrar
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
        </div>
      </form>
    </section>
  );
}

